import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import {
  Loader2, ArrowRight, Clock, Newspaper, Users, Sparkles, Drama, Ticket, Flame, Trophy, BookOpen,
} from "lucide-react";
import { Seo } from "@/components/Seo";
import { InteractiveCards } from "@/components/InteractiveCards";
import { ActorAvatar } from "@/components/ActorAvatar";
import { fmtDate } from "@/lib/date";

function SectionHeader({ icon: Icon, title, to, accent }) {
  return (
    <div className="mb-4 flex items-center justify-between">
      <div className="flex items-center gap-2.5">
        <div className={`flex h-8 w-8 items-center justify-center rounded-full ${accent}`}>
          <Icon className="h-4 w-4" />
        </div>
        <h2 className="font-serif text-2xl font-semibold text-foreground">{title}</h2>
      </div>
      {to && (
        <Link to={to} className="flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground">
          See all <ArrowRight className="h-3.5 w-3.5" />
        </Link>
      )}
    </div>
  );
}

function ShowCard({ show, urgent }) {
  return (
    <Link
      to={`/shows/${show.slug}`}
      className={`group flex-shrink-0 w-56 overflow-hidden rounded-xl border bg-card transition-all hover:-translate-y-1 ${
        urgent ? "border-primary/40 hover:border-primary" : "border-border hover:border-primary"
      }`}
    >
      <div className="relative aspect-[3/4] w-full overflow-hidden bg-secondary">
        {show.image_url ? (
          <img src={show.image_url} alt={show.name} className="h-full w-full object-cover transition-transform duration-300 group-hover:scale-105" />
        ) : (
          <div className="flex h-full items-center justify-center text-muted-foreground">No image</div>
        )}
        {urgent && (
          <div className="absolute top-2 right-2 rounded-full bg-[#9E1B28] px-2.5 py-1 text-[10px] font-semibold uppercase tracking-wide text-amber-50 shadow-lg">
            Closing Soon
          </div>
        )}
      </div>
      <div className="p-3">
        <div className="truncate font-serif text-base text-foreground">{show.name}</div>
        <div className="truncate text-xs text-muted-foreground">{show.venue}</div>
        {show.booking_until && (
          <div className={`mt-1 text-xs font-medium ${urgent ? "text-primary" : "text-[hsl(var(--gold))]"}`}>
            Booking until {show.booking_until}
          </div>
        )}
      </div>
    </Link>
  );
}

// ---------------------------------------------------------------------------
// Matching a news headline to one of your shows (so we can show its poster)
// ---------------------------------------------------------------------------
const norm = (s) =>
  (s || "").toLowerCase().replace(/&/g, " and ").replace(/[^a-z0-9]+/g, " ").trim();

// One-word, short show names (Wicked, Six, Chicago...) also turn up in ordinary news,
// so for those the headline must also look theatre-related.
const THEATRE_WORDS = /\b(west end|musical|london|theatre|stage|cast|casting|tour|broadway|play|production|opening night)\b/;

function buildMatcher(shows) {
  const entries = [];
  (shows || []).forEach((s) => {
    const full = norm(s.name);
    if (!full) return;
    const variants = new Set([full]);
    if (full.startsWith("the ")) variants.add(full.slice(4));
    const beforeColon = norm((s.name || "").split(":")[0]);
    if (beforeColon) variants.add(beforeColon);
    variants.forEach((v) => {
      if (v.length >= 3) entries.push({ v, show: s, weak: !v.includes(" ") && v.length <= 8 });
    });
  });
  entries.sort((a, b) => b.v.length - a.v.length); // longest name wins
  return (title) => {
    const t = norm(title);
    const padded = ` ${t} `;
    for (const e of entries) {
      if (!padded.includes(` ${e.v} `)) continue;
      if (e.weak && !THEATRE_WORDS.test(t)) continue;
      return e.show;
    }
    return null;
  };
}

function Poster({ show, className = "" }) {
  return (
    <div className={`relative flex-shrink-0 overflow-hidden bg-gradient-to-br from-[#2a1620] to-[#0D0A0E] ${className}`}>
      {show?.image_url ? (
        <img src={show.image_url} alt={show.name || ""} loading="lazy" className="h-full w-full object-cover" />
      ) : (
        <div className="flex h-full w-full items-center justify-center">
          <Drama className="h-1/3 w-1/3 text-[#C89B3C]/60" aria-hidden="true" />
        </div>
      )}
    </div>
  );
}

function FeatureNews({ n, show }) {
  return (
    <a
      href={n.link}
      target="_blank"
      rel="noreferrer"
      className="group flex h-full gap-4 overflow-hidden rounded-2xl border border-border bg-card p-4 transition-colors hover:border-primary sm:gap-5 sm:p-5"
    >
      <Poster show={show} className="aspect-[3/4] w-28 rounded-xl sm:w-40" />
      <div className="flex min-w-0 flex-1 flex-col">
        <div className="text-xs uppercase tracking-wide text-[hsl(var(--gold))]">{n.source}</div>
        <h3 className="mt-2 font-serif text-xl font-semibold leading-snug text-foreground group-hover:underline sm:text-2xl">
          {n.title}
        </h3>
        {n.summary && <p className="mt-2 line-clamp-3 text-sm text-muted-foreground">{n.summary}</p>}
        <div className="mt-auto flex items-center justify-between pt-3 text-xs text-muted-foreground">
          <span className="truncate">{show ? show.name : ""}</span>
          <span className="flex items-center gap-1 text-foreground">Read <ArrowRight className="h-3.5 w-3.5" /></span>
        </div>
      </div>
    </a>
  );
}

function CompactNews({ n, show }) {
  return (
    <a
      href={n.link}
      target="_blank"
      rel="noreferrer"
      className="flex gap-3 rounded-xl border border-border bg-card p-3 transition-colors hover:border-primary"
    >
      <Poster show={show} className="aspect-[3/4] w-14 rounded-lg" />
      <div className="min-w-0">
        <div className="text-xs uppercase tracking-wide text-[hsl(var(--gold))]">{n.source}</div>
        <div className="mt-0.5 line-clamp-3 text-sm font-medium text-foreground">{n.title}</div>
      </div>
    </a>
  );
}

// ---------------------------------------------------------------------------
// Recent cast changes
// ---------------------------------------------------------------------------
const TAGS = {
  joins: { label: "Joins", cls: "bg-emerald-500/15 text-emerald-400" },
  leaves: { label: "Leaves", cls: "bg-primary/15 text-primary" },
};

function CastChangeCard({ e, show, photo }) {
  const tag = TAGS[e.change_type] || { label: "Update", cls: "bg-secondary text-muted-foreground" };
  const today = new Date().toISOString().slice(0, 10);
  const future = (e.change_date || "").slice(0, 10) > today;
  return (
    <Link
      to={`/shows/${show?.slug || e.show_id}`}
      className="group flex w-72 flex-shrink-0 gap-3 rounded-xl border border-border bg-card p-3 transition-all hover:-translate-y-1 hover:border-primary"
    >
      <Poster show={show} className="aspect-[3/4] w-16 rounded-lg" />
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2">
          <span className={`rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide ${tag.cls}`}>
            {tag.label}
          </span>
          {e.change_date && (
            <span className="truncate text-xs text-muted-foreground">
              {future ? "From " : ""}{fmtDate(e.change_date)}
            </span>
          )}
        </div>
        <div className="mt-2 flex items-center gap-2">
          {e.actor_name && <ActorAvatar name={e.actor_name} photoUrl={photo} size={32} />}
          <div className="min-w-0">
            {e.actor_name && <div className="truncate text-sm font-medium text-foreground">{e.actor_name}</div>}
            {e.role && <div className="truncate text-xs text-muted-foreground">{e.role}</div>}
          </div>
        </div>
        <div className="mt-2 line-clamp-2 text-xs text-muted-foreground">
          {e.show_name}
        </div>
      </div>
    </Link>
  );
}

// ---------------------------------------------------------------------------
// Daily Theatre Trivia card
// ---------------------------------------------------------------------------
function TriviaCard({ t }) {
  const me = t.me;
  let line = "Five new questions every day. How many can you get right?";
  let cta = "Play today's quiz";
  if (t.status === "completed") {
    line = `You scored ${t.score}/5 today. Come back tomorrow for a new quiz.`;
    cta = "See your answers";
  } else if (t.status === "in_progress") {
    line = "You've started today's quiz. Pick up where you left off.";
    cta = "Continue";
  }
  return (
    <Link
      to="/trivia"
      data-testid="home-trivia-card"
      className="group relative flex flex-col gap-4 overflow-hidden rounded-2xl border border-[#C89B3C]/40 bg-gradient-to-r from-[#2a1620] via-[#1a1218] to-[#0D0A0E] p-5 transition-colors hover:border-[#C89B3C] sm:flex-row sm:items-center sm:justify-between sm:p-7"
    >
      <div className="flex items-start gap-4">
        <div className="flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-full bg-[#C89B3C]/15">
          <Drama className="h-6 w-6 text-[#C89B3C]" aria-hidden="true" />
        </div>
        <div>
          <div className="text-xs font-semibold uppercase tracking-widest text-[#C89B3C]">Daily Theatre Trivia</div>
          <div className="mt-1 font-serif text-xl font-semibold text-amber-50 sm:text-2xl">
            Today's quiz is ready
          </div>
          <p className="mt-1 text-sm text-amber-100/70">{line}</p>
          {me && (me.current_streak > 0 || me.completed > 0) && (
            <p className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-amber-100/70">
              <span className="flex items-center gap-1"><Flame className="h-3.5 w-3.5 text-[#C89B3C]" /> {me.current_streak} day streak</span>
              <span className="flex items-center gap-1"><Trophy className="h-3.5 w-3.5 text-[#C89B3C]" /> {me.completed} played</span>
            </p>
          )}
        </div>
      </div>
      <span className="inline-flex items-center justify-center gap-2 self-start rounded-full bg-[#C89B3C] px-6 py-3 text-sm font-medium text-black transition-opacity group-hover:opacity-90 sm:self-center">
        {cta} <ArrowRight className="h-4 w-4" />
      </span>
    </Link>
  );
}

function MyTheatreCard() {
  const { user } = useAuth();
  const [counts, setCounts] = useState(null);
  useEffect(() => {
    if (!user) { setCounts(null); return; }
    let active = true;
    Promise.all([api.get("/dashboard"), api.get("/diary")])
      .then(([d, y]) => active && setCounts({ want: (d.data.want_list || []).length, diary: y.data?.stats?.total || 0 }))
      .catch(() => active && setCounts(null));
    return () => { active = false; };
  }, [user]);

  let title = "Your theatre diary, shared with friends";
  let line = "Log every show you've seen, rate it and keep your memories. Add friends to see their diary and feed, and save shows you want to see. Free.";
  let cta = "Start your diary";
  if (user) {
    title = "My Theatre";
    cta = "Open My Theatre";
    if (counts) {
      line = `${counts.want} on your watchlist · ${counts.diary} in your diary.`;
      if (counts.diary === 0) { line += " Log your first show."; cta = "Log your first show"; }
    } else {
      line = "Your watchlist and theatre diary, in one place.";
    }
  }
  return (
    <Link
      to={user ? "/watchlist" : "/login"}
      data-testid="home-mytheatre-card"
      className="group relative flex flex-col gap-4 overflow-hidden rounded-2xl border border-[#C89B3C]/40 bg-gradient-to-r from-[#1a1218] via-[#2a1620] to-[#1a1218] p-5 transition-colors hover:border-[#C89B3C] sm:flex-row sm:items-center sm:justify-between sm:p-7"
    >
      <div className="flex items-start gap-4">
        <div className="flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-full bg-[#C89B3C]/15">
          <BookOpen className="h-6 w-6 text-[#C89B3C]" aria-hidden="true" />
        </div>
        <div>
          <div className="text-xs font-semibold uppercase tracking-widest text-[#C89B3C]">My Theatre</div>
          <div className="mt-1 font-serif text-xl font-semibold text-amber-50 sm:text-2xl">{title}</div>
          <p className="mt-1 max-w-xl text-sm text-amber-100/70">{line}</p>
        </div>
      </div>
      <span className="inline-flex items-center justify-center gap-2 self-start rounded-full bg-[#C89B3C] px-6 py-3 text-sm font-medium text-black transition-opacity group-hover:opacity-90 sm:self-center">
        {cta} <ArrowRight className="h-4 w-4" />
      </span>
    </Link>
  );
}

export default function Home() {
  const [loading, setLoading] = useState(true);
  const [comingSoon, setComingSoon] = useState([]);
  const [closingSoon, setClosingSoon] = useState([]);
  const [news, setNews] = useState([]);
  const [castEvents, setCastEvents] = useState([]);
  const [allShows, setAllShows] = useState([]);
  const [totalShows, setTotalShows] = useState(0);
  const [trivia, setTrivia] = useState(null);
  const [photos, setPhotos] = useState({});
  const { user } = useAuth();

  useEffect(() => {
    let active = true;
    async function load() {
      try {
        const [closing, coming, newsRes, casts, shows] = await Promise.all([
          api.get("/shows", { params: { status: "Closing Soon" } }),
          api.get("/shows", { params: { status: "Coming Soon" } }),
          api.get("/news", { params: { page: 1, page_size: 5 } }),
          api.get("/cast-changes/recent"),
          api.get("/shows"),
        ]);
        if (!active) return;
        setClosingSoon(closing.data.slice(0, 6));
        setComingSoon(coming.data.slice(0, 6));
        setNews(newsRes.data.items || newsRes.data || []);
        setCastEvents((casts.data || []).slice(0, 8));
        setAllShows(shows.data || []);
        setTotalShows((shows.data || []).length);
      } catch (e) {
        console.error("Failed to load home page data", e);
      } finally {
        if (active) setLoading(false);
      }
    }
    load();
    return () => { active = false; };
  }, []);

  // Extras that must never stop the page loading: today's trivia, and performer photos.
  useEffect(() => {
    let active = true;
    api.get("/trivia/today")
      .then((r) => { if (active && r.data?.available) setTrivia(r.data); })
      .catch(() => {});
    api.get("/actors")
      .then((r) => {
        if (!active) return;
        const m = {};
        (r.data || []).forEach((a) => { if (a.name && a.photo_url) m[a.name.toLowerCase()] = a.photo_url; });
        setPhotos(m);
      })
      .catch(() => {});
    return () => { active = false; };
  }, []);

  const matchShow = useMemo(() => buildMatcher(allShows), [allShows]);
  const showById = useMemo(() => {
    const m = {};
    allShows.forEach((s) => { m[s.id] = s; });
    return m;
  }, [allShows]);

  if (loading) {
    return (
      <div className="flex min-h-[60vh] items-center justify-center">
        <Loader2 className="h-6 w-6 animate-spin text-[#C89B3C]" />
      </div>
    );
  }

  const heroImages = [...closingSoon, ...comingSoon].filter((s) => s.image_url).slice(0, 4);
  const newsWithShows = news.map((n) => ({ n, show: matchShow(n.title) }));

  return (
    <div>
      <Seo
        title="Compare West End Ticket Prices & Cast Changes"
        description="Compare West End ticket prices across sellers, follow shows and casts, keep a free theatre diary of every show you've seen and share it with friends."
      />
      {/* Hero with blurred poster collage background (always dark) */}
      <section className="relative overflow-hidden border-b border-amber-900/20">
        <div className="absolute inset-0 flex">
          {heroImages.length > 0 ? (
            heroImages.map((s, i) => (
              <img key={i} src={s.image_url} alt="" className="h-full w-1/4 scale-125 object-cover opacity-60 blur-2xl" />
            ))
          ) : (
            <div className="h-full w-full bg-[#161118]" />
          )}
        </div>
        <div className="absolute inset-0 bg-gradient-to-b from-[#0D0A0E]/50 via-[#0D0A0E]/75 to-[#0D0A0E]" />
        <div className="grain absolute inset-0" />

        <div className="relative z-10 mx-auto grid max-w-7xl gap-10 px-4 py-16 sm:px-6 sm:py-24 lg:grid-cols-[minmax(0,1fr)_380px] lg:items-center lg:gap-14">
          <div>
            <h1 className="max-w-3xl font-serif text-4xl font-semibold text-amber-50 sm:text-5xl">
              <span className="text-[#C89B3C]">Compare West End ticket prices.</span>{" "}
              Track the shows and actors you care about.
            </h1>
            <p className="mt-4 max-w-xl text-lg text-amber-100/70">
              Follow West End shows and casts, get alerts the moment something changes, then keep a free diary of every show you’ve seen and share it with your friends.
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Link to="/shows" className="rounded-full bg-[#9E1B28] px-6 py-3 text-sm font-medium text-amber-50 hover:bg-[#B22232]">
                Browse Shows
              </Link>
              <Link
                to={user ? "/watchlist" : "/login"}
                className="rounded-full bg-[#C89B3C] px-6 py-3 text-sm font-medium text-black hover:opacity-90"
              >
                {user ? "My Theatre" : "Start your diary"}
              </Link>
            </div>

            <div className="mt-10 flex flex-wrap gap-x-8 gap-y-3 text-sm text-amber-100/60">
              <span className="flex items-center gap-2"><Drama className="h-4 w-4 text-[#C89B3C]" /> {totalShows} shows tracked</span>
              <span className="flex items-center gap-2"><Ticket className="h-4 w-4 text-[#C89B3C]" /> Multiple sellers compared</span>
              <span className="flex items-center gap-2"><Users className="h-4 w-4 text-[#C89B3C]" /> Live cast-change alerts</span>
              <span className="flex items-center gap-2"><BookOpen className="h-4 w-4 text-[#C89B3C]" /> Free diary, shared with friends</span>
            </div>
          </div>

          <InteractiveCards />
        </div>
      </section>

      <div className="mx-auto max-w-7xl px-4 py-12 sm:px-6">
        <section className="mb-6">
          <MyTheatreCard />
        </section>

        {trivia && (
          <section className="mb-14">
            <TriviaCard t={trivia} />
          </section>
        )}

        {comingSoon.length > 0 && (
          <section className="mb-14">
            <SectionHeader icon={Sparkles} title="Coming Soon" to="/shows?status=Coming Soon" accent="bg-[hsl(var(--gold))]/20 text-[hsl(var(--gold))]" />
            <div className="flex gap-4 overflow-x-auto pb-2">
              {comingSoon.map((s) => <ShowCard key={s.id} show={s} />)}
            </div>
          </section>
        )}

        {closingSoon.length > 0 && (
          <section className="mb-14">
            <SectionHeader icon={Clock} title="Closing Soon" to="/shows?status=Closing Soon" accent="bg-primary/15 text-primary" />
            <div className="flex gap-4 overflow-x-auto pb-2">
              {closingSoon.map((s) => <ShowCard key={s.id} show={s} urgent />)}
            </div>
          </section>
        )}

        {newsWithShows.length > 0 && (
          <section className="mb-14" data-testid="home-news">
            <SectionHeader icon={Newspaper} title="Latest News" to="/news" accent="bg-secondary text-foreground" />
            <div className="grid gap-4 lg:grid-cols-[minmax(0,1.15fr)_minmax(0,1fr)]">
              <FeatureNews n={newsWithShows[0].n} show={newsWithShows[0].show} />
              <div className="grid gap-3">
                {newsWithShows.slice(1).map(({ n, show }) => (
                  <CompactNews key={n.link} n={n} show={show} />
                ))}
              </div>
            </div>
          </section>
        )}

        {castEvents.length > 0 && (
          <section data-testid="home-cast-changes">
            <SectionHeader icon={Users} title="Recent Cast Changes" to="/casts" accent="bg-secondary text-foreground" />
            <div className="flex gap-4 overflow-x-auto pb-2">
              {castEvents.map((e) => (
                <CastChangeCard
                  key={e.id}
                  e={e}
                  show={showById[e.show_id]}
                  photo={photos[(e.actor_name || "").toLowerCase()]}
                />
              ))}
            </div>
          </section>
        )}
      </div>
    </div>
  );
}